import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, WifiOff } from 'lucide-react';
import {
  JOB_EVENT,
  estimateRemainingMs,
  formatDuration,
  hasStayRequiredJob,
  jobPercent,
  type JobSnapshot,
} from '../lib/jobProgress';
import { cn } from './cn';

function isDismiss(detail: unknown): boolean {
  return !!detail && typeof detail === 'object' && (detail as { dismiss?: boolean }).dismiss === true;
}

/**
 * Global measured-job banners. One place so every export/upload/import/download
 * can report progress without each screen inventing a spinner.
 */
export const JobProgressHost: React.FC = () => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState<JobSnapshot[]>([]);
  const [online, setOnline] = useState(() => typeof navigator === 'undefined' || navigator.onLine);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const onJob = (event: Event) => {
      const detail = (event as CustomEvent<JobSnapshot & { dismiss?: boolean }>).detail;
      if (!detail) return;
      setJobs((prev) => {
        if (isDismiss(detail)) return prev.filter((j) => j.id !== detail.id);
        const next = prev.filter((j) => j.id !== detail.id);
        next.push(detail);
        return next;
      });
    };
    window.addEventListener(JOB_EVENT, onJob);
    return () => window.removeEventListener(JOB_EVENT, onJob);
  }, []);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, []);

  const visible = jobs.filter((j) => j.status === 'running' || j.status === 'error' || j.status === 'done');
  const stay = visible.some((j) => j.status === 'running' && j.stayRequired);

  useEffect(() => {
    if (!stay) return undefined;
    const onLeave = (e: BeforeUnloadEvent) => {
      if (!hasStayRequiredJob()) return;
      e.preventDefault();
      e.returnValue = t('jobs.stayWarning');
      return t('jobs.stayWarning');
    };
    window.addEventListener('beforeunload', onLeave);
    return () => window.removeEventListener('beforeunload', onLeave);
  }, [stay, t]);

  useEffect(() => {
    if (!visible.some((j) => j.status === 'running')) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [visible]);

  if (visible.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4">
      <div className="pointer-events-auto w-full max-w-lg space-y-2" aria-live="polite">
        {visible.map((job) => {
          const percent = jobPercent(job);
          const remaining = estimateRemainingMs(job.current, job.total, job.startedAt, now);
          const needsNet = job.status === 'running' && job.requiresOnline && !online;
          return (
            <div
              key={job.id}
              role="status"
              className="rounded-panel border border-hairline bg-surface p-4 shadow-lg"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-ink">{job.label}</p>
                  {job.message && <p className="mt-0.5 text-[0.8125rem] text-ink-secondary">{job.message}</p>}
                </div>
                {job.status === 'running' && (
                  <span className="num shrink-0 text-sm font-semibold text-ink">
                    {percent == null ? '…' : t('jobs.percent', { percent })}
                  </span>
                )}
                {job.status === 'done' && (
                  <span className="text-sm font-semibold text-positive">{t('jobs.done')}</span>
                )}
                {job.status === 'error' && (
                  <span className="text-sm font-semibold text-negative">{t('jobs.error')}</span>
                )}
              </div>
              <div
                className="mt-3 h-2 overflow-hidden rounded-full bg-surface-sunken"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={percent ?? undefined}
                aria-label={job.label}
              >
                {percent == null && job.status === 'running' ? (
                  <div className="h-full w-1/3 animate-pulse rounded-full bg-brand-600" />
                ) : (
                  <div
                    className={cn(
                      'h-full rounded-full transition-[width]',
                      job.status === 'error' ? 'bg-negative' : 'bg-brand-600',
                    )}
                    style={{ width: `${percent ?? 0}%` }}
                  />
                )}
              </div>
              <p className="mt-2 text-[0.75rem] text-ink-muted">
                {t('jobs.elapsed', { time: formatDuration(now - job.startedAt) })}
                {remaining != null && job.status === 'running' && (
                  <> · {t('jobs.remaining', { time: formatDuration(remaining) })}</>
                )}
              </p>
              {job.status === 'running' && job.stayRequired && (
                <p className="mt-2 flex items-start gap-1.5 text-[0.8125rem] text-caution">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                  {t('jobs.stayWarning')}
                </p>
              )}
              {needsNet && (
                <p className="mt-2 flex items-start gap-1.5 text-[0.8125rem] text-caution">
                  <WifiOff size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
                  {t('jobs.offline')}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const JobBusyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Loader2 size={16} className={cn('animate-spin', className)} aria-hidden="true" />
);
